/**
 * Tesoreria › Da sistemare (3.00, docs/analisi/proposta-da-sistemare-chiusura-anno.md):
 * the bank movements, card spending and invoices still to settle, by load. The
 * state is worked out from what exists already; only the holds are stored.
 */
import { z } from 'zod';
import { reconciliationCheck } from './reconciliation.js';
import { transferPending } from './transfers.js';
import { idSchema, isoDateSchema, optionalText, requiredText } from './validation.js';
export const WORK_ITEM_KINDS = [
    'movimento',
    'spesa_carta',
    'fattura',
    'rata',
    'autofattura',
];
/** The filter of the list: «aperti» is all but the settled ones. */
export const WORKLIST_STATE_FILTERS = ['aperti', 'da_fare', 'sospesi', 'scaduti'];
/** The table of Tabelle with the reasons of a hold. */
export const HOLD_REASONS_TABLE = 'motivi-sospensione';
/** A movement is settled when reconciled and nothing of it is left to explain. */
export function movementSettled(amount, reconciliation) {
    return (reconciliation !== null &&
        reconciliationCheck(amount, reconciliation) === null &&
        // A transfer is settled with its other movement (3.13).
        !transferPending(reconciliation));
}
/**
 * A spending is settled when accounted for; with only its item while its shop
 * expects an invoice, the invoice is still to link (1.57), unless it was said
 * that none will come (3.22).
 */
export function cardSpendingSettled(reconciliation, expectsInvoice) {
    if (!reconciliation)
        return false;
    return !(reconciliation.kind === 'conto' && expectsInvoice && !reconciliation.noInvoice);
}
/** The documents to settle: never pro forma, cancelled, replaced or self-billing ones. */
export function invoiceInWorklist(invoice) {
    return (invoice.kind !== 'proforma' &&
        !invoice.cancelledAt &&
        !invoice.replacedAt &&
        !invoice.selfBilling);
}
/**
 * The self-billing to link to the payment of the foreign supplier (3.17): the
 * received side only, Gestify has each one also among the issued documents.
 */
export function selfBillingInWorklist(invoice) {
    return (invoice.selfBilling &&
        invoice.direction === 'ricevuta' &&
        invoice.kind === 'fattura' &&
        !invoice.cancelledAt &&
        !invoice.replacedAt);
}
/**
 * What an invoice lacks: its subject, its item of the plan, and its commessa
 * when it concerns the commesse (`jobStatus` null: it does not).
 */
export function invoiceMissing(invoice) {
    const missing = [];
    if (!invoice.subjectId)
        missing.push('anagrafica');
    if (!invoice.reclassCode)
        missing.push('voce');
    if (invoice.jobStatus === 'da_imputare' || invoice.jobStatus === 'parziale') {
        missing.push('commessa');
    }
    return missing;
}
/** The state of an item: a hold counts until it is settled, and is late after its day. */
export function workState(settled, hold, today) {
    if (settled)
        return 'sistemato';
    if (!hold)
        return 'da_fare';
    return hold.remindOn && hold.remindOn < today ? 'sospeso_scaduto' : 'sospeso';
}
export const holdCreateSchema = z.object({
    kind: z.enum(WORK_ITEM_KINDS),
    itemId: idSchema,
    reasonCode: requiredText(30),
    note: optionalText(500).default(null),
    remindOn: isoDateSchema.nullish().transform((value) => value ?? null),
});
export const holdUpdateSchema = z.object({
    reasonCode: requiredText(30).optional(),
    note: optionalText(500).optional(),
    remindOn: isoDateSchema.nullable().optional(),
});
export const worklistQuerySchema = z.object({
    kind: z.enum(WORK_ITEM_KINDS).optional(),
    state: z.enum(WORKLIST_STATE_FILTERS).default('aperti'),
    year: z.coerce.number().int().min(2000).max(2100).optional(),
    load: z.string().max(80).optional(),
    /** The loads: also those with nothing left (by default only the open ones). */
    all: z
        .enum(['true', 'false'])
        .optional()
        .transform((value) => value === 'true'),
});
//# sourceMappingURL=worklist.js.map